import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import type { Quotation, SubmittedQuotation } from '~/api/quotations/types';

import { HttpNotFoundError } from '~/api/errors';
import { QueryKeys } from '~/api/keys';
import { useDownloadQuotationCoFiles, useQuotations } from '~/api/quotations';
import { AlertDialog, Button, ErrorState, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import { ApproveQuotationDialog } from './components/ApproveQuotationDialog';
import { ConfirmDeleteQuotationDialog } from './components/ConfirmDeleteQuotationDialog';
import { RejectQuotationDialog } from './components/RejectQuotationDialog';
import { usePrefetchNextPage, useQuotationsTableColumns } from './QuotationsTable.hooks';
import {
  useQuotationsTableColumnFilters,
  useQuotationsTableGlobalFilter,
  useQuotationsTablePaginationState,
  useQuotationsTableSorting,
} from './QuotationsTable.nuqs';

export const QuotationsTable = () => {
  const [pagination, setPagination] = useQuotationsTablePaginationState();
  const [sorting, setSorting] = useQuotationsTableSorting();
  const [globalFilter, setGlobalFilter] = useQuotationsTableGlobalFilter();
  const [columnFilters, setColumnFilters] = useQuotationsTableColumnFilters();

  const [debouncedGlobalFilter] = useDebouncedState(globalFilter, 200);
  const queryMetadata = useMemo(
    () => ({
      pagination,
      sorting,
      filters: {
        search: debouncedGlobalFilter.trim(),
        clientId: (columnFilters.find((filter) => filter.id === ColumnIds.quotationsClient)?.value ?? null) as number | null,
        brandId: (columnFilters.find((filter) => filter.id === ColumnIds.quotationsBrand)?.value ?? null) as number | null,
        year: (columnFilters.find((filter) => filter.id === ColumnIds.quotationsDocYear)?.value ?? null) as number[] | null,
        quarter: (columnFilters.find((filter) => filter.id === ColumnIds.quotationsDocQuarter)?.value ?? null) as string[] | null,
        status: (columnFilters.find((filter) => filter.id === ColumnIds.quotationsStatus)?.value ?? null) as
          SubmittedQuotation['status'][] | null,
      },
    }),
    [debouncedGlobalFilter, columnFilters, pagination, sorting],
  );

  const { data, isError, isPending, isPlaceholderData } = useQuotations(queryMetadata);
  const queryClient = useQueryClient();

  usePrefetchNextPage(data?.pagination.nextPage ?? null, queryMetadata);

  const downloadCoFilesMutation = useDownloadQuotationCoFiles();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'quotations']);

  const approveQuotationHandle = AlertDialog.useHandle<Quotation>();
  const rejectQuotationHandle = AlertDialog.useHandle<Quotation>();
  const deleteQuotationHandle = AlertDialog.useHandle<Quotation>();
  const dialogHandles = useMemo(
    () => ({ approveQuotationHandle, rejectQuotationHandle, deleteQuotationHandle }),
    [approveQuotationHandle, rejectQuotationHandle, deleteQuotationHandle],
  );

  const columns = useQuotationsTableColumns({ dialogHandles });
  const [table, Table] = useTable({
    data: data?.data ?? [],
    columns,
    initialState: {
      columnVisibility: {
        [ColumnIds.quotationsBrand]: false,
        [ColumnIds.quotationsDocYear]: false,
        [ColumnIds.quotationsDocQuarter]: false,
      },
    },
    state: { pagination, sorting, globalFilter, columnFilters },
    enableRowSelection: (row) => row.original.status !== 'Draft',
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: (...args) => {
      setGlobalFilter(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnFiltersChange: (...args) => {
      setColumnFilters(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    rowCount: data?.pagination.totalResults,
  });

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          queryClient.resetQueries({ queryKey: QueryKeys.quotations.index.metadata(queryMetadata) });
          setPagination(null);
          setSorting(null);
          setGlobalFilter(null);
          setColumnFilters(null);
        }}
        title={t('quotations:overview.error.title')}
      />
    );
  }

  return (
    <>
      <Table.Root table={table}>
        <Table.Menu>
          <Table.Search />

          <Table.Filters>
            <Table.Filter columnId={ColumnIds.quotationsClient} type="combobox" />
            <Table.Filter columnId={ColumnIds.quotationsBrand} hideWhen="singleOption" type="select" />
            <Table.Filter columnId={ColumnIds.quotationsDocYear} type="multiselect" />
            <Table.Filter columnId={ColumnIds.quotationsDocQuarter} type="multiselect" />
            <Table.Filter columnId={ColumnIds.quotationsStatus} type="multiselect" />
          </Table.Filters>
        </Table.Menu>

        <Table.Grid isLoading={isPending || isPlaceholderData}>
          <Table.Header />

          <Table.Body>
            {(row) => (
              <Table.Row
                actionLabel={t('common:actions.edit')}
                onClick={match(row.original.status)
                  .with('Draft', () => () => navigate(r(routes.createQuotation, { quotationId: row.id })))
                  .with(Pattern.union('Sent', 'Expired'), () => () => navigate(r(routes.editQuotation, { quotationId: row.id })))
                  .with(Pattern.union('Approved', 'Rejected'), () => undefined)
                  .exhaustive()}
                row={row}
              />
            )}
          </Table.Body>
        </Table.Grid>

        <Table.Pagination showPageSizeOptions />

        <Table.Selection>
          {(selectedRowIds) => (
            <Button
              loading={downloadCoFilesMutation.isPending}
              onClick={() =>
                downloadCoFilesMutation.mutate(
                  { ids: selectedRowIds },
                  {
                    onError: (error, { ids }) =>
                      toast.danger({
                        description:
                          error instanceof HttpNotFoundError
                            ? t('quotations:alerts.missingFcFiles', { count: ids.length })
                            : t('common:error'),
                      }),
                  },
                )
              }
            >
              {`${t('common:datatable.download')} (${selectedRowIds.length})`}
            </Button>
          )}
        </Table.Selection>
      </Table.Root>

      <AlertDialog.Root handle={approveQuotationHandle}>
        {({ payload: quotation }) =>
          quotation && <ApproveQuotationDialog onClose={() => approveQuotationHandle.close()} quotationId={quotation.id} />
        }
      </AlertDialog.Root>

      <AlertDialog.Root handle={rejectQuotationHandle}>
        {({ payload: quotation }) =>
          quotation && <RejectQuotationDialog onClose={() => rejectQuotationHandle.close()} quotationId={quotation.id} />
        }
      </AlertDialog.Root>

      <AlertDialog.Root handle={deleteQuotationHandle}>
        {({ payload: quotation }) =>
          quotation && <ConfirmDeleteQuotationDialog onClose={() => deleteQuotationHandle.close()} quotationId={quotation.id} />
        }
      </AlertDialog.Root>
    </>
  );
};
